import dayjs from "dayjs";
import { RelativeComponentLoader } from "@/components/loader";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { useResourceOpportunities } from "@/pages/sql-warehouses/hooks/useDatabricksOpportunities";
import { InsightSection } from "@/modules/insights";
import type { ResourceType } from "@/pages/explorer/types";

const CLUSTER_RESOURCE_TYPE = 12 as ResourceType;

interface Props {
  clusterId: string;
  clusterName?: string;
  instanceId?: number;
  workspaceName?: string;
  workspaceId?: string;
}

const ClusterOpportunities = ({ clusterId, instanceId, workspaceId }: Props) => {
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const selectedOpportunityId = searchParams.get("opportunityId") || "";

  const {
    startDate: opportunityStartDate,
    endDate: opportunityEndDate,
    onUpdate: onDateRangeChange,
  } = useDateFromSearchParams(
    dayjs().subtract(1, "month").toDate(),
    dayjs().toDate(),
    "opportunity"
  );

  const resourceUri = clusterId;

  const opportunities = useResourceOpportunities({
    resourceUri,
    resourceType: CLUSTER_RESOURCE_TYPE,
    instanceId: instanceId?.toString(),
    workspaceId,
    startDate: opportunityStartDate,
    endDate: opportunityEndDate,
  });

  const setSelectedOpportunityId = (opportunityId: string) => {
    updateSearchParamsByKey({ opportunityId });
  };

  if (opportunities.isFetching) {
    return <RelativeComponentLoader label="Loading opportunities..." componentHeight={40} />;
  }

  return (
    <InsightSection
      opportunities={opportunities.data?.data ?? []}
      selectedOpportunityId={selectedOpportunityId}
      setOpportunityId={setSelectedOpportunityId}
      startDate={opportunityStartDate}
      endDate={opportunityEndDate}
      onDateRangeChange={onDateRangeChange}
      emptyTitle="No opportunities"
      emptySubTitle="No opportunities found for this cluster"
    />
  );
};

export default ClusterOpportunities;
